import {
  showMessageNotification,
  useBlockNavigation,
  useDeleteProviderPhoto,
  useProviderPhotos,
  useUploadProviderPhoto
} from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarProviderPhoto, type SugarProviderPhotoList } from '@app/types'
import { Alert, Box, Button, Checkbox, Group, LoadingOverlay, Stack, Text } from '@mantine/core'
import { IconChecks } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { type FC, useCallback, useMemo, useReducer, useRef, useState } from 'react'

import { mapPhotoUploadError } from './mapPhotoUploadError'
import { PhotoGallery } from './PhotoGallery'
import { PhotoGuidelinesPanel } from './PhotoGuidelinesPanel'
import { PhotoRemoveConfirmModal, type RemoveIntent } from './PhotoRemoveConfirmModal'
import { PhotoSaveConfirmModal } from './PhotoSaveConfirmModal'
import { stagedReducer } from './stagedReducer'

const ATTESTATION_TEXT =
  'By submitting photos, I attest that I have the right to upload and share these images for public display on the Enroll / DPP family search tool. The photos comply with DPP photo guidelines and do not include identifiable images of children or any personal or sensitive information. Any individuals shown (e.g., staff) have provided appropriate consent. I understand that submitted photos may be reviewed and removed if they do not meet program requirements.'

export const PhotosTab: FC = () => {
  const { siteId } = useSugarContext()
  const queryClient = useQueryClient()
  const { data: photoList, error, isError, isFetching } = useProviderPhotos()
  const { isUploadingPhoto, uploadPhoto } = useUploadProviderPhoto()
  const { deletePhoto, isDeletingPhoto } = useDeleteProviderPhoto()

  const [staged, dispatch] = useReducer(stagedReducer, [])
  const [attested, setAttested] = useState(false)
  const [confirmingSave, setConfirmingSave] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<{ photo: SugarProviderPhoto; intent: RemoveIntent } | null>(null)
  const addPickerRef = useRef<() => void | undefined>(null)

  const photos = photoList?.photos ?? []
  const limits = photoList?.limits
  const cap = limits?.maxPhotosPerProfile ?? 3
  // Server-counted cap: Active + Pending toward cap; Invalid excluded. Staged
  // adds count locally so the FE reflects the limit during a batch session.
  const currentCount = (limits?.currentActiveCount ?? 0) + (limits?.currentPendingCount ?? 0)
  const atCap = currentCount + staged.length >= cap

  const anyUploading = useMemo(() => staged.some((s) => s.status === 'uploading'), [staged])
  const hasIdleStaged = useMemo(() => staged.some((s) => s.status === 'idle'), [staged])
  useBlockNavigation(anyUploading)

  const handleAdd = useCallback((file: File) => {
    dispatch({ type: 'add', payload: { file } })
  }, [])

  const handleRemoveStaged = useCallback((id: string) => {
    dispatch({ type: 'remove', payload: { id } })
  }, [])

  const openRemoveModal = useCallback((photo: SugarProviderPhoto) => {
    setRemoveTarget({ intent: 'remove', photo })
  }, [])

  const openReplaceModal = useCallback((photo: SugarProviderPhoto) => {
    setRemoveTarget({ intent: 'replace', photo })
  }, [])

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeTarget) return
    const { intent, photo } = removeTarget
    try {
      await deletePhoto(photo.id)
      if (intent === 'replace') {
        // Optimistically remove the deleted photo from the cache so atCap
        // re-derives to false before the picker opens. Without this, the
        // post-delete refetch (isFetching=true) keeps addDisabled=true and
        // Dropzone's openRef is a no-op while disabled.
        queryClient.setQueryData<SugarProviderPhotoList>(
          ['provider-photos', { siteId }],
          (prev) => {
            if (!prev) return prev
            const wasActive = photo.status === 'Active'
            const wasPending = photo.status === 'Under Review'
            return {
              photos: prev.photos.filter((p) => p.id !== photo.id),
              limits: {
                ...prev.limits,
                currentActiveCount: wasActive
                  ? Math.max(0, prev.limits.currentActiveCount - 1)
                  : prev.limits.currentActiveCount,
                currentPendingCount: wasPending
                  ? Math.max(0, prev.limits.currentPendingCount - 1)
                  : prev.limits.currentPendingCount
              }
            }
          }
        )
        // Immediately reopen the Add picker for the replacement file.
        setRemoveTarget(null)
        // Defer to next tick so the modal closes before the file dialog opens.
        setTimeout(() => addPickerRef.current?.(), 0)
      } else {
        setRemoveTarget(null)
      }
    } catch (err) {
      showMessageNotification(mapPhotoUploadError(err))
      setRemoveTarget(null)
    }
  }, [deletePhoto, queryClient, removeTarget, siteId])

  const handleSaveClick = useCallback(() => {
    setConfirmingSave(true)
  }, [])

  const handleSaveConfirm = useCallback(async () => {
    setConfirmingSave(false)
    // Sequential uploads — matches the AC's "one at a time" language and keeps
    // per-tile spinner state intuitive.
    for (const s of staged) {
      if (s.status !== 'idle') continue
      dispatch({ type: 'markUploading', payload: { id: s.id } })
      try {
        await uploadPhoto({ file: s.file })
        dispatch({ type: 'markUploaded', payload: { id: s.id } })
      } catch (err) {
        const mapped = mapPhotoUploadError(err)
        dispatch({ type: 'markFailed', payload: { id: s.id, message: mapped.message } })
      }
    }
  }, [staged, uploadPhoto])

  const handleRetry = useCallback(async (id: string) => {
    const target = staged.find((s) => s.id === id)
    if (!target || target.status !== 'error') return
    dispatch({ type: 'markUploading', payload: { id } })
    try {
      await uploadPhoto({ file: target.file })
      dispatch({ type: 'markUploaded', payload: { id } })
    } catch (err) {
      const mapped = mapPhotoUploadError(err)
      dispatch({ type: 'markFailed', payload: { id, message: mapped.message } })
    }
  }, [staged, uploadPhoto])

  const isBusy = isFetching || isUploadingPhoto || isDeletingPhoto
  const saveDisabled = !attested || !hasIdleStaged || anyUploading || isFetching
  const newPhotoCount = staged.filter((s) => s.status === 'idle').length

  return (
    <Box pos='relative'>
      <LoadingOverlay radius='lg' visible={isFetching && !photoList} />
      <Stack spacing='lg'>
        <PhotoGuidelinesPanel />

        {isError && (
          <Alert color='red'>
            {error instanceof Error ? error.message : 'Could not load photos.'}
          </Alert>
        )}

        <PhotoGallery
          addDisabled={isUploadingPhoto}
          addOpenRef={addPickerRef}
          atCap={atCap}
          cap={cap}
          isBusy={isBusy}
          onAdd={handleAdd}
          onRemoveServer={openRemoveModal}
          onRemoveStaged={handleRemoveStaged}
          onReplaceServer={openReplaceModal}
          onRetryStaged={handleRetry}
          photos={photos}
          staged={staged}
        />

        <Checkbox
          checked={attested}
          color='brand-dark-blue'
          description={ATTESTATION_TEXT}
          label='I attest that the photos I am uploading comply with DPP photo guidelines.'
          onChange={(ev) => setAttested(ev.currentTarget.checked)}
        />

        <Group position='right'>
          <Button
            color='green.8'
            disabled={saveDisabled}
            leftIcon={<IconChecks size={18} />}
            loading={anyUploading}
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </Group>

        {!attested && hasIdleStaged && (
          <Text aria-live='polite' c='dimmed' size='xs' ta='right'>
            Check the attestation above to enable Save.
          </Text>
        )}
      </Stack>

      <PhotoSaveConfirmModal
        count={newPhotoCount}
        onCancel={() => setConfirmingSave(false)}
        onConfirm={handleSaveConfirm}
        opened={confirmingSave}
      />

      <PhotoRemoveConfirmModal
        intent={removeTarget?.intent ?? 'remove'}
        isPending={isDeletingPhoto}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={handleRemoveConfirm}
        opened={removeTarget !== null}
        photo={removeTarget?.photo ?? null}
      />
    </Box>
  )
}
